import { css } from '@emotion/react';
import { Box, Button, Group, NativeSelect } from '@inflearn/ds-react-next';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import type { ChangeEvent } from 'react';

import { API_ENDPOINT } from '../../../../resources/@types/apiEndpoint';
import {
  type ClientApiV1PostsUserUserIdQueryString,
  fetchClientApiV1PostsUserUserId,
  useClientApiV1PostsUserUserIdQuery,
} from '../../../../resources/@types/uccSchema';
import { logger } from '../../../../resources/Logger';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import Pagination from '../../../components/pages/profile/Pagination/Pagination';
import PostItem from '../../../components/pages/profile/PostItem/PostItem';
import PostSkeletonItem from '../../../components/pages/profile/PostItem/PostSkeletonItem';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { QUERY_STRING } from '../../../constants';
import { POST_FILTER, POST_SORT } from '../../../constants/post';
import { INFLEARN_URL } from '../../../constants/urls';
import useCheckBlackUser from '../../../hooks/useCheckBlackUser';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import { useCustomQueryParams } from '../../../hooks/useCustomQueryParams';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import { generateQueryKeyUrl } from '../../../utils/generateQueryKeyUrl';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';
import {
  PostItemType,
  normalizePostSort,
  normalizePostType,
} from '../../../utils/post/postItemType';

type Props = {
  isApiError?: boolean;
};

const DEFAULT_QUERY_PARAMS: ClientApiV1PostsUserUserIdQueryString = {
  type: 'ALL',
  sort: 'RECENT',
  pageSize: 10,
  pageNumber: 1,
};

const DEFAULT_EN_QUERY_PARAMS: ClientApiV1PostsUserUserIdQueryString = {
  type: 'ANSWER',
  sort: 'RECENT',
  pageSize: 10,
  pageNumber: 1,
};

const getDefaultQueryParam = (lang: string) =>
  lang === 'ko' ? DEFAULT_QUERY_PARAMS : DEFAULT_EN_QUERY_PARAMS;

const UserPostsPage: WithCommonNextPage<Props> = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const { isMe } = useCheckMyProfile();
  const { isBlackUser } = useCheckBlackUser();

  const { queryParams, push } = useCustomQueryParams<ClientApiV1PostsUserUserIdQueryString>({
    queryParamKeys: ['pageNumber', 'pageSize', 'sort', 'type'],
    defaultValue: getDefaultQueryParam(lang),
  });

  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });

  const type = normalizePostType(queryParams.type);
  const sort = normalizePostSort(queryParams.sort);
  const pageNumber = Number(queryParams.pageNumber);

  const {
    data: posts,
    isError,
    isLoading,
    refetch,
  } = useClientApiV1PostsUserUserIdQuery({
    ...generateQueryKeyUrl({
      url: API_ENDPOINT.CLIENT_V1_POSTS_USER_USERID(String(profile?.id)),
      queryParams,
    }),
    options: {
      keepPreviousData: true,
    },
  });

  const onChangeType = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    if (isTypeQueryString(value)) {
      push({ pageNumber: 1, type: value });
    }
  };

  const onChangeSort = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    if (isSortQueryString(value)) {
      push({ pageNumber: 1, sort: value });
    }
  };

  const postsTotalPage = posts?.totalPage ?? 1;

  const userName = profile?.name;

  const postSortOptions = Object.values(POST_SORT).map((option) => ({
    ...option,
    label: t(option.label),
  }));
  const postFilterOptions = Object.values(POST_FILTER).map((option) => ({
    ...option,
    label: t(option.label),
  }));

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        robots={!isBlackUser}
        title={t('{{userName}}님의 게시글', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 게시글 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />
      <SectionHeader styles={css({ marginBottom: '30px !important' })}>
        <SectionHeader.Title title={t('게시글')} />
        {type === PostItemType.ALL && isEmpty(posts?.items) ? null : (
          <Group gap={8}>
            <KoOnly>
              <NativeSelect
                defaultValue={DEFAULT_QUERY_PARAMS.type}
                value={type}
                onChange={onChangeType}
                data={postFilterOptions}
              />
            </KoOnly>

            <NativeSelect
              defaultValue={DEFAULT_QUERY_PARAMS.sort}
              value={sort}
              onChange={onChangeSort}
              data={postSortOptions}
            />
          </Group>
        )}
      </SectionHeader>
      <Group w="100%" mih={300} justify="center" align="flex-start">
        <EmptyStates
          height={300}
          isEmpty={!isLoading && isEmpty(posts?.items)}
          isError={Boolean(isError)}
          onRetry={refetch}
          empty={{
            title: t('작성한 게시글이 없어요', { context: 'public' }),
            body:
              isMe && type === PostItemType.ALL && isEmpty(posts?.items)
                ? t('이후 활동한 내역이 있을 때 볼 수 있어요!')
                : null,
            cta:
              isMe && type === PostItemType.ALL && isEmpty(posts?.items) ? (
                <KoOnly>
                  <Button
                    component="a"
                    variant="default"
                    size="sm"
                    radius="md"
                    href={INFLEARN_URL.COMMUNITY_QUESTIONS()}
                  >
                    {t('커뮤니티 둘러보기')}
                  </Button>
                </KoOnly>
              ) : null,
          }}
        >
          <Box css={{ width: '100%', flex: 1, flexGrow: 1 }}>
            <Box
              w="100%"
              component="ul"
              css={[
                {
                  minHeight: 602,

                  li: {
                    borderBottom: `1px solid ${'var(--mantine-color-gray-2)'}`,
                    '&:last-of-type': {
                      borderBottom: 'none',
                    },
                  },
                },
              ]}
            >
              {isLoading ? (
                <DeferredComponent>
                  <PostSkeletonItem count={5} />
                </DeferredComponent>
              ) : (
                posts?.items?.map((post, index) => (
                  <li key={`${post.id}${index}`}>
                    <PostItem {...post} />
                  </li>
                ))
              )}
            </Box>
            {postsTotalPage === DEFAULT_QUERY_PARAMS.pageNumber ? null : (
              <Pagination
                value={pageNumber}
                total={postsTotalPage}
                onChange={(value) => push({ pageNumber: value }, { scroll: true })}
              />
            )}
          </Box>
        </EmptyStates>
      </Group>
    </Layout>
  );
};
export const getServerSideProps = withProfileUserProps(async ({ context, queryClient }) => {
  const userId = context.params?.id ? String(context.params?.id) : '';

  const locale = context.locale as string;
  const type = normalizePostType(context.query[QUERY_STRING.TYPE]);
  const sort = normalizePostSort(context.query[QUERY_STRING.SORT]);
  const pageNumber = Number(context.query[QUERY_STRING.PAGE]);

  try {
    const queryParams = {
      ...getDefaultQueryParam(locale),
      ...(pageNumber && { pageNumber }),
      ...(type && { type }),
      ...(sort && { sort }),
    };

    const queryOptions = generateQueryKeyUrl({
      url: API_ENDPOINT.CLIENT_V1_POSTS_USER_USERID(userId),
      queryParams,
    });

    await queryClient.prefetchQuery(queryOptions.queryKey, () =>
      fetchClientApiV1PostsUserUserId(queryOptions.url),
    );

    return {
      props: {},
    };
  } catch (e) {
    logger.info(`[ERROR in SSR posts:] ${e}`);

    return {
      props: {
        isError: true,
      },
    };
  }
});

const isTypeQueryString = (
  value: unknown,
): value is ClientApiV1PostsUserUserIdQueryString['type'] =>
  ['QUESTION', 'ANSWER', 'CHAT', 'STUDY', 'PROJECT', 'ALL'].includes(String(value));

const isSortQueryString = (
  value: unknown,
): value is ClientApiV1PostsUserUserIdQueryString['sort'] =>
  ['RECOMMEND', 'RECENT', 'OLDER'].includes(String(value));

export default withRemovedPageFallback(UserPostsPage);
